import { useMutation } from "@tanstack/react-query";
import { Link2, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { COMMERCIAL_DOCUMENT_LABELS } from "../../shared/commercial-documents";
import { hubContract, type HubDocument } from "../../shared/tool-hubs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { documentDate } from "../knowledge/customer-document-format";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";
import { linkDocumentToHub } from "./tool-hubs-api";

type ContractProps = {
  organizationId: string;
  customerSlug: string;
  hubId: string;
  documents: { linked: HubDocument[]; suggested: HubDocument[] } | null;
  canManage: boolean;
  navigate: OperationsNavigate;
  onLinked: () => void;
};

function DocumentRow({
  document,
  customerSlug,
  navigate,
}: {
  document: HubDocument;
  customerSlug: string;
  navigate: OperationsNavigate;
}) {
  return (
    <OperationsLink
      className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-1 px-6 py-4 no-underline hover:bg-muted/60"
      href={`/customers/${customerSlug}/documents/${document.id}`}
      navigate={navigate}
    >
      <span className="w-32 shrink-0 text-sm font-semibold text-muted-foreground">
        {COMMERCIAL_DOCUMENT_LABELS[document.kind]}
      </span>
      <span className="min-w-0 flex-1 text-foreground">
        <span className="font-semibold">{document.reference}</span>
        <span className="text-muted-foreground">
          {" "}
          · {document.title} · version {document.revision}
        </span>
      </span>
      {document.filing ? (
        <Badge variant="success">Signed {documentDate(document.filing.signedOn)}</Badge>
      ) : (
        <Badge variant="secondary">Not signed</Badge>
      )}
    </OperationsLink>
  );
}

function EmptySlot({ label, text, action }: { label: string; text: string; action: ReactNode }) {
  return (
    <div className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 px-6 py-4">
      <span className="w-32 shrink-0 text-sm font-semibold text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 text-muted-foreground">{text}</span>
      {action}
    </div>
  );
}

function NewDocumentButton({
  kind,
  customerSlug,
  navigate,
}: {
  kind: "order_form" | "change_order";
  customerSlug: string;
  navigate: OperationsNavigate;
}) {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => {
        navigate(`/customers/${customerSlug}/documents/new/${kind}`);
      }}
    >
      <Plus /> New {COMMERCIAL_DOCUMENT_LABELS[kind].toLowerCase()}
    </Button>
  );
}

/** Every tool is sold with an order form and may gain change orders (ADR-0088). */
export function ToolHubContract(props: ContractProps) {
  const { documents, customerSlug, navigate, canManage } = props;
  if (!documents)
    return (
      <ContractFrame>
        <p className="px-6 py-5 text-muted-foreground">
          Your employee permissions do not include this tool’s contract.
        </p>
      </ContractFrame>
    );
  const { orderForm, changeOrders } = hubContract(documents.linked);
  const button = (kind: "order_form" | "change_order") =>
    canManage ? (
      <NewDocumentButton kind={kind} customerSlug={customerSlug} navigate={navigate} />
    ) : null;
  return (
    <ContractFrame>
      <div className="divide-y divide-muted">
        {orderForm ? (
          <DocumentRow document={orderForm} customerSlug={customerSlug} navigate={navigate} />
        ) : (
          <EmptySlot label="Order form" text="No order form yet" action={button("order_form")} />
        )}
        {changeOrders.map((document) => (
          <DocumentRow
            key={document.id}
            document={document}
            customerSlug={customerSlug}
            navigate={navigate}
          />
        ))}
        {changeOrders.length === 0 || canManage ? (
          <EmptySlot
            label="Change orders"
            text={changeOrders.length ? `${changeOrders.length} so far` : "None"}
            action={button("change_order")}
          />
        ) : null}
      </div>
      <SuggestedDocuments {...props} suggested={documents.suggested} />
    </ContractFrame>
  );
}

function ContractFrame({ children }: { children: ReactNode }) {
  return (
    <section aria-labelledby="tool-contract-title" className="space-y-3">
      <h3 id="tool-contract-title" className="text-xl font-bold">
        Contract
      </h3>
      <div className="overflow-hidden rounded-xl bg-card shadow-card">{children}</div>
    </section>
  );
}

function SuggestedDocuments({
  organizationId,
  hubId,
  suggested,
  canManage,
  onLinked,
}: ContractProps & { suggested: HubDocument[] }) {
  const link = useMutation({
    mutationFn: (documentId: string) => linkDocumentToHub(organizationId, documentId, hubId),
    onSuccess: onLinked,
  });
  if (!canManage || !suggested.length) return null;
  return (
    <div className="space-y-3 border-t border-muted bg-info-background px-6 py-4">
      <p className="font-semibold">These documents name this tool. Add them to its contract?</p>
      <ul className="space-y-2">
        {suggested.map((document) => (
          <li key={document.id} className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {COMMERCIAL_DOCUMENT_LABELS[document.kind]} {document.reference} · {document.title}
              {document.filing ? " · signed" : ""}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={link.isPending}
              onClick={() => {
                link.mutate(document.id);
              }}
            >
              <Link2 /> Add to this tool
            </Button>
          </li>
        ))}
      </ul>
      {link.error ? <FieldError>{link.error.message}</FieldError> : null}
    </div>
  );
}
